// ── ImportApprovalPopup — Approval modal for applying scope manifest to any tenant ───
import React from 'react';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import {
  Users,
  AlertTriangle,
  Eye,
  X,
  ArrowRight,
  Filter,
  Flame,
  Archive,
  Database,
  Layers,
  HardDrive,
} from 'lucide-react';
import type {
  ScopeManifest,
  ImportResult,
  Tenant,
  ManifestOutcome,
} from '../types';

import { fmtBytes as formatBytes } from '@/utils/format';

interface ImportApprovalPopupProps {
  filteredManifest: ScopeManifest;
  tenantList: Tenant[];
  activeTenantId: number | '';

  targetTenantId: number | '';
  setTargetTenantId: (v: number | '') => void;
  strategy: 'skip' | 'upsert' | 'overwrite';
  setStrategy: (v: 'skip' | 'upsert' | 'overwrite') => void;

  dryRunResult: ImportResult | null;
  dryRunPending: boolean;
  dryRunError: string | null;
  onDryRun: () => void;

  importPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ImportApprovalPopup({
  filteredManifest,
  tenantList,
  activeTenantId,
  targetTenantId,
  setTargetTenantId,
  strategy,
  setStrategy,
  dryRunResult,
  dryRunPending,
  dryRunError,
  onDryRun,
  importPending,
  onConfirm,
  onClose,
}: ImportApprovalPopupProps) {
  const scopeCount = filteredManifest.scopes.length;
  const outcomeCount = filteredManifest.outcomes?.length ?? 0;
  const hotStreamCount = filteredManifest.hot_stream?.length ?? 0;
  const nsSet = new Set(filteredManifest.scopes.map((s) => s.scope_namespace));
  const nsCounts = Array.from(nsSet).map((ns) => ({
    ns,
    count: filteredManifest.scopes.filter((s) => s.scope_namespace === ns).length,
  }));
  const handlerCount = new Set(
    (filteredManifest.outcomes ?? []).map((o: ManifestOutcome) => o.handler_key),
  ).size;
  const archiveFileCount = filteredManifest.hot_archive_files?.length ?? 0;

  const gcsFileCount = filteredManifest.gcs_files?.length ?? 0;

  useEscapeLayer(onClose, !importPending);

  const canConfirm =
    targetTenantId !== '' &&
    typeof targetTenantId === 'number' &&
    targetTenantId > 0 &&
    !importPending;

  const targetTenant = tenantList.find(
    (t) => t.idTenant === targetTenantId,
  );

  return (
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-20 flex items-center justify-center p-4">
      <div className="pac-glass-raised rounded-lg p-5 max-w-lg w-full space-y-4 shadow-2xl max-h-[90%] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-100">
            <Users className="w-4 h-4 text-violet-400" />
            Apply to Tenant
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 text-xs">
          {/* ── Data Layers Topology ─────────────────────────────────── */}
          <div>
            <div className="text-neutral-500 mb-1.5 flex items-center gap-1.5">
              <Layers className="w-3 h-3" />
              Data Layers
            </div>
            <div className="space-y-1.5">
              {/* Layer 1: Scopes — entity table */}
              <div className="pac-glass rounded px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                  <div>
                    <div className="text-[10px] text-neutral-300 font-medium">Scopes</div>
                    <div className="text-[9px] text-neutral-600">Entity registry &amp; family relations</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-neutral-200 font-medium tabular-nums">{scopeCount}</div>
                  <div className="text-[9px] text-neutral-600 tabular-nums">{nsSet.size} ns · {handlerCount} handler{handlerCount !== 1 ? 's' : ''}</div>
                </div>
              </div>

              {/* Layer 2: Outcomes — mid/warm data */}
              <div className="pac-glass rounded px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <div>
                    <div className="text-[10px] text-emerald-300 font-medium">Outcomes</div>
                    <div className="text-[9px] text-neutral-600">Configuration &amp; warm data registers</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={'text-sm font-medium tabular-nums ' + (outcomeCount > 0 ? 'text-emerald-300' : 'text-neutral-600')}>
                    {outcomeCount}
                  </div>
                </div>
              </div>

              {/* Layer 3: Hot Stream — 7-day live data */}
              <div className="pac-glass rounded px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <div>
                    <div className="text-[10px] text-amber-300 font-medium flex items-center gap-1">
                      <Flame className="w-2.5 h-2.5" />
                      Hot Stream
                    </div>
                    <div className="text-[9px] text-neutral-600">Live 7-day hot data table registers</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={'text-sm font-medium tabular-nums ' + (hotStreamCount > 0 ? 'text-amber-300' : 'text-neutral-600')}>
                    {hotStreamCount}
                  </div>
                  <div className="text-[9px] text-neutral-600 tabular-nums">row{hotStreamCount !== 1 ? 's' : ''}</div>
                </div>
              </div>

              {/* Layer 4: Bucket Artifacts — GCS objects referenced in values */}
              <div className="pac-glass rounded px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  <div>
                    <div className="text-[10px] text-rose-300 font-medium flex items-center gap-1">
                      <HardDrive className="w-2.5 h-2.5" />
                      Bucket Artifacts
                    </div>
                    <div className="text-[9px] text-neutral-600">GCS objects referenced in outcome &amp; hot stream values</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={'text-sm font-medium tabular-nums ' + (gcsFileCount > 0 ? 'text-rose-300' : 'text-neutral-600')}>
                    {gcsFileCount}
                  </div>
                  <div className="text-[9px] text-neutral-600 tabular-nums">gs:// object{gcsFileCount !== 1 ? 's' : ''}</div>
                </div>
              </div>

              {/* Layer 5: Archives — parquet cold storage */}
              <div className="pac-glass rounded px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                  <div>
                    <div className="text-[10px] text-sky-300 font-medium flex items-center gap-1">
                      <Archive className="w-2.5 h-2.5" />
                      Archives
                    </div>
                    <div className="text-[9px] text-neutral-600">Parquet history completing hot stream</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={'text-sm font-medium tabular-nums ' + (archiveFileCount > 0 ? 'text-sky-300' : 'text-neutral-600')}>
                    {archiveFileCount}
                  </div>
                  <div className="text-[9px] text-neutral-600 tabular-nums">file{archiveFileCount !== 1 ? 's' : ''}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Per-namespace breakdown */}
          <div>
            <div className="text-neutral-500 mb-1 flex items-center gap-1.5">
              <Filter className="w-3 h-3" />
              Namespaces ({nsSet.size})
            </div>
            <div className="pac-glass rounded p-2 space-y-1 max-h-32 overflow-y-auto">
              {nsCounts.map(({ ns, count }) => (
                <div
                  key={ns}
                  className="flex items-center justify-between text-[11px]"
                >
                  <span className="text-neutral-300 font-mono truncate">
                    {ns}
                  </span>
                  <span className="text-neutral-500 tabular-nums shrink-0 ml-2">
                    {count} scope{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-neutral-800" />

          {/* Target tenant picker */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-neutral-500 text-[11px] shrink-0">
                Target tenant
              </span>
              <select
                value={targetTenantId}
                onChange={(e) =>
                  setTargetTenantId(
                    e.target.value ? Number(e.target.value) : '',
                  )
                }
                className="flex-1 px-2 py-1 rounded pac-glass-input text-neutral-300 text-xs outline-none focus:border-violet-500"
              >
                <option value="">— select tenant —</option>
                {tenantList.map((t) => (
                    <option key={t.idTenant} value={t.idTenant}>
                      {t.tenantName} (#{t.idTenant}){t.idTenant === activeTenantId ? ' ★ active' : ''}
                    </option>
                  ))}
              </select>
            </div>

            {/* Active-tenant confirmation badge */}
            {targetTenantId === activeTenantId && activeTenantId !== '' && (
              <div className="flex items-center gap-1.5 text-[10px] text-violet-400/80 px-1">
                <Users className="w-3 h-3" />
                Importing into the currently active tenant
              </div>
            )}

            {/* Strategy */}
            <div className="flex items-center gap-2">
              <span className="text-neutral-500 text-[11px] shrink-0">
                Strategy
              </span>
              <select
                value={strategy}
                onChange={(e) =>
                  setStrategy(
                    e.target.value as 'skip' | 'upsert' | 'overwrite',
                  )
                }
                className="flex-1 px-2 py-1 rounded pac-glass-input text-neutral-300 text-xs outline-none focus:border-violet-500"
              >
                <option value="upsert">Upsert (create + update)</option>
                <option value="skip">Skip existing</option>
                <option value="overwrite">Overwrite</option>
              </select>
            </div>
          </div>

          {/* Warning banner */}
          {canConfirm && (
            <div className="flex items-start gap-1.5 bg-amber-950/40 border border-amber-900/50 rounded p-2">
              <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-[10px] text-amber-400/90 leading-relaxed">
                <span className="font-medium">{scopeCount}</span> scope{scopeCount !== 1 ? 's' : ''}
                {outcomeCount > 0 && (
                  <>, <span className="text-emerald-400 font-medium">{outcomeCount}</span> outcome{outcomeCount !== 1 ? 's' : ''}</>
                )}
                {hotStreamCount > 0 && (
                  <>, <span className="text-amber-300 font-medium">{hotStreamCount}</span> hot stream row{hotStreamCount !== 1 ? 's' : ''}</>
                )}
                {gcsFileCount > 0 && (
                  <>, <span className="text-rose-300 font-medium">{gcsFileCount}</span> GCS object{gcsFileCount !== 1 ? 's' : ''}</>                )}
                {archiveFileCount > 0 && (
                  <>, <span className="text-sky-300 font-medium">{archiveFileCount}</span> archive file{archiveFileCount !== 1 ? 's' : ''}</>
                )}{' '}
                will be imported into{' '}
                <span className="font-mono font-medium text-amber-300">
                  {targetTenant
                    ? targetTenant.tenantName
                    : `tenant ${targetTenantId}`}
                </span>{' '}
                using{' '}
                <span className="font-medium">{strategy}</span> strategy.
                {hotStreamCount === 0 && archiveFileCount === 0 && (
                  <span className="block mt-1 text-neutral-500">
                    No hot stream or archive data in manifest. Enable <span className="text-amber-500">Include hot stream data</span> during export to migrate live data.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Dry-run section */}
          {canConfirm && (
            <div className="space-y-1.5">
              <button
                className={
                  'px-2 py-1 rounded border text-[11px] flex items-center gap-1 ' +
                  (dryRunPending
                    ? 'border-neutral-700 text-neutral-500 cursor-wait'
                    : 'border-violet-800 text-violet-300 hover:border-violet-600')
                }
                disabled={dryRunPending}
                onClick={onDryRun}
              >
                <Eye className="w-3 h-3" />
                {dryRunPending ? 'Running dry-run…' : 'Preview (dry-run)'}
              </button>

              {dryRunResult && (
                <div className="pac-glass rounded p-3 space-y-2.5 text-[10px]">
                  <div className="text-neutral-400 font-medium flex items-center gap-1.5">
                    <Database className="w-3 h-3" />
                    Dry-run result — per data layer
                  </div>

                  {/* ── Layer 1: Scopes ─────────────────────────── */}
                  <div className="pac-glass rounded p-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-[9px] text-violet-400 uppercase tracking-wider font-medium">
                      <div className="w-1 h-1 rounded-full bg-violet-500" />
                      Scopes
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-2.5">
                      <span className="text-neutral-500">Create</span>
                      <span className="text-emerald-400 tabular-nums font-mono">
                        {dryRunResult.scopes_created}
                      </span>
                      <span className="text-neutral-500">Existing</span>
                      <span className="text-neutral-400 tabular-nums font-mono">
                        {dryRunResult.scopes_existing}
                      </span>
                      {(dryRunResult.scopes_skipped ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Skipped</span>
                          <span className="text-neutral-500 tabular-nums font-mono">
                            {dryRunResult.scopes_skipped}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── Layer 2: Outcomes ───────────────────────── */}
                  <div className="pac-glass rounded p-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-[9px] text-emerald-400 uppercase tracking-wider font-medium">
                      <div className="w-1 h-1 rounded-full bg-emerald-500" />
                      Outcomes
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-2.5">
                      <span className="text-neutral-500">Write</span>
                      <span className="text-emerald-400 tabular-nums font-mono">
                        {dryRunResult.outcomes_written}
                      </span>
                      {(dryRunResult.outcomes_updated ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Update</span>
                          <span className="text-amber-400 tabular-nums font-mono">
                            {dryRunResult.outcomes_updated}
                          </span>
                        </>
                      )}
                      {(dryRunResult.outcomes_skipped ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Skipped</span>
                          <span className="text-neutral-500 tabular-nums font-mono">
                            {dryRunResult.outcomes_skipped}
                          </span>
                        </>
                      )}
                      {(dryRunResult.outcomes_deleted ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Delete</span>
                          <span className="text-rose-400 tabular-nums font-mono">
                            {dryRunResult.outcomes_deleted}
                          </span>
                        </>
                      )}
                    </div>
                    {dryRunResult.outcomes_written === 0 &&
                      (dryRunResult.outcomes_updated ?? 0) === 0 &&
                      outcomeCount === 0 && (
                        <div className="text-[9px] text-neutral-600 pl-2.5 italic">
                          No outcomes in manifest — export with "Include outcomes" enabled.
                        </div>
                      )}
                  </div>

                  {/* ── Layer 3: Hot Stream ─────────────────────── */}
                  <div className="pac-glass rounded p-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-[9px] text-amber-400 uppercase tracking-wider font-medium">
                      <div className="w-1 h-1 rounded-full bg-amber-500" />
                      <Flame className="w-2.5 h-2.5" />
                      Hot Stream
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-2.5">
                      <span className="text-neutral-500">Write</span>
                      <span className={(dryRunResult.hot_stream_written ?? 0) > 0 ? 'text-amber-400 tabular-nums font-mono' : 'text-neutral-600 tabular-nums font-mono'}>
                        {dryRunResult.hot_stream_written ?? 0}
                      </span>
                    </div>
                    {(dryRunResult.hot_stream_written ?? 0) === 0 && hotStreamCount === 0 && (
                      <div className="text-[9px] text-neutral-600 pl-2.5 italic">
                        No hot stream data in manifest — export with "Include hot stream" enabled to migrate 7-day live data.
                      </div>
                    )}
                  </div>

                  {/* ── Layer 4: Bucket Artifacts ──────────────── */}
                  <div className="pac-glass rounded p-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-[9px] text-rose-400 uppercase tracking-wider font-medium">
                      <div className="w-1 h-1 rounded-full bg-rose-500" />
                      <HardDrive className="w-2.5 h-2.5" />
                      Bucket Artifacts
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-2.5">
                      <span className="text-neutral-500">Copied</span>
                      <span className={(dryRunResult.gcs_files_copied ?? 0) > 0 ? 'text-rose-400 tabular-nums font-mono' : 'text-neutral-600 tabular-nums font-mono'}>
                        {dryRunResult.gcs_files_copied ?? 0}
                      </span>
                      {(dryRunResult.gcs_files_skipped ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Skipped (hash match)</span>
                          <span className="text-neutral-400 tabular-nums font-mono">
                            {dryRunResult.gcs_files_skipped}
                          </span>
                        </>
                      )}
                      {(dryRunResult.gcs_files_failed ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Failed</span>
                          <span className="text-red-400 tabular-nums font-mono">
                            {dryRunResult.gcs_files_failed}
                          </span>
                        </>
                      )}
                      {(dryRunResult.gcs_bytes_transferred ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Bytes transferred</span>
                          <span className="text-rose-400/70 tabular-nums font-mono">
                            {formatBytes(dryRunResult.gcs_bytes_transferred ?? 0)}
                          </span>
                        </>
                      )}
                    </div>
                    {(dryRunResult.gcs_files_copied ?? 0) === 0 && (dryRunResult.gcs_files_skipped ?? 0) === 0 && gcsFileCount === 0 && (
                      <div className="text-[9px] text-neutral-600 pl-2.5 italic">
                        No gs:// bucket artifacts detected in outcome or hot stream values.
                      </div>
                    )}
                  </div>

                  {/* ── Layer 5: Archives ───────────────────────── */}
                  <div className="pac-glass rounded p-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-[9px] text-sky-400 uppercase tracking-wider font-medium">
                      <div className="w-1 h-1 rounded-full bg-sky-500" />
                      <Archive className="w-2.5 h-2.5" />
                      Archives
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-2.5">
                      <span className="text-neutral-500">Copied</span>
                      <span className={(dryRunResult.hot_archive_files_copied ?? 0) > 0 ? 'text-sky-400 tabular-nums font-mono' : 'text-neutral-600 tabular-nums font-mono'}>
                        {dryRunResult.hot_archive_files_copied ?? 0}
                      </span>
                      {(dryRunResult.hot_archive_skipped ?? 0) > 0 && (
                        <>
                          <span className="text-neutral-500">Skipped (hash match)</span>
                          <span className="text-neutral-400 tabular-nums font-mono">
                            {dryRunResult.hot_archive_skipped}
                          </span>
                        </>
                      )}
                    </div>
                    {(dryRunResult.hot_archive_files_copied ?? 0) === 0 && (dryRunResult.hot_archive_skipped ?? 0) === 0 && archiveFileCount === 0 && (
                      <div className="text-[9px] text-neutral-600 pl-2.5 italic">
                        No archive files in manifest — parquet history unavailable for this export.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {dryRunError && (
                <div className="text-[10px] text-red-400">{dryRunError}</div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-300 hover:border-neutral-500"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={
              'px-3 py-1.5 rounded border text-xs flex items-center gap-1.5 ' +
              (canConfirm
                ? 'border-violet-700 text-violet-200 hover:border-violet-500'
                : 'border-neutral-800 text-neutral-600 cursor-not-allowed')
            }
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            <ArrowRight className="w-3 h-3" />
            {importPending
              ? 'Importing…'
              : canConfirm
                ? `Import → ${targetTenant ? targetTenant.tenantName : `Tenant ${targetTenantId}`}`
                : 'Select a tenant'}
          </button>
        </div>
      </div>
    </div>
  );
}
